/**
 * AUDIT ACTION → HEBREW. 09 §9.1 V3. **X10.**
 *
 * The office's audit table has a `מה` column — *what happened* — and the server never sent anything
 * to put in it. The wire shape declared `actionHe`, a rendered Hebrew sentence, and `actionHe`
 * existed in exactly one place in the whole tree: `fixtures.ts`. So in a fixtures-off build the
 * office's own audit trail painted rows whose `מה` cell was EMPTY while the table around it rendered
 * fully — an audit surface that looks present and populated with the one column saying what happened
 * blank. The type said `string` and the runtime value was `undefined`: nothing faulted, nothing
 * warned, nobody looked.
 *
 * ── THE RULED TRANSPORT, AND WHY THE HEBREW IS HERE AND NOT ON THE SERVER ───────────────────────
 *
 * The server emits the STABLE `action` CODE. `eventUid`, `occurredAtUtc`, `actorDisplayName` and
 * `action` cross the wire; the Hebrew stays at the edge, in this module, beside `denialText.ts` —
 * exactly as `denyReason` already does one column over on the same table. That is the whole shape of
 * the fix: a stable code on the wire, the words where the words are rendered. One owner for this
 * screen's Hebrew. A server-side mapping would have put the same strings in two layers, which is the
 * drift the wave carries a guardrail against.
 *
 * ── WHAT IS MAPPED, AND WHY THE LIST IS WIDER THAN THE CLASS-B INVENTORY ────────────────────────
 *
 * `TENANT_VISIBLE_AUDIT_ACTIONS` — fifteen codes, enumerated from the PRODUCERS. The 21-site class-B
 * inventory (`audited-operation-atomicity.test.ts`) is the right denominator for atomicity and the
 * wrong one for visibility: it counts `#recordAudit` call sites, so it omits the gate's
 * `authz.allowed` / `authz.denied`, which carry the tenant (`packages/authz/src/gate.ts:261`), are
 * the MAJORITY of the rows an office reads, and are the ONLY rows that can populate the
 * `הסיבה לדחייה` column at all — `domain-audit.ts:90` nulls `denyReason` on every domain fact. A
 * mapping built from the class-B list alone would leave `מה` blank on exactly the rows an office
 * most needs.
 *
 * Sixteen further codes are deliberately UNNAMED, with the reason recorded beside
 * `PLATFORM_ONLY_AUDIT_ACTIONS` in `packages/contracts/src/vocabulary.ts`: they are written under a
 * PLATFORM session or are structurally null-tenant, so `audit-sink.ts`'s `WHERE tenant_id = ?` can
 * never return them to a tenant. There is no row to euphemise, so no euphemism exists.
 *
 * ── THE FALLBACK IS NOT A LICENCE FOR A PARTIAL MAPPING ─────────────────────────────────────────
 *
 * An unmapped code renders `פעולה שאינה מזוהה` — neither blank nor the machine code, the same rule
 * `TenantSecurityScreen` already honours for deny reasons. It degrades ONE CELL honestly instead of
 * lying. It is not permission for the mapping to be incomplete: the mapping is asserted TOTAL over
 * the vocabulary by `contract.mirror.test.ts`, and the vocabulary is asserted total over the
 * producers by `apps/api/src/composition/audit-action-vocabulary.test.ts`. A new audited action
 * cannot ship unnamed.
 *
 * This module is pure. It maps codes to words. It grants nothing and it checks nothing.
 *
 * COPY OWNER: the Product Lead — PRODUCT-EXPECTATION_GLPOINT-S4W2-X5-X8-COPY.md, ITEM 2. Fifteen
 * names and one fallback, verbatim. Nothing here is invented and nothing here is a second
 * vocabulary: `מתן גישה לחברה` and `פתיחת חברה` are `fixtures.ts`'s own words reused, so the
 * office's wording does not change when fixtures go off; `אישור הרשאת תמיכה` /
 * `ביטול הרשאת תמיכה` match the screen's own buttons; `עדכון מדיניות אימות דו-שלבי` is X5's
 * confirmed copy.
 */

import { TENANT_VISIBLE_AUDIT_ACTIONS, type TenantVisibleAuditAction } from './contract';

/**
 * An `action` code with no Hebrew name. **09 §9.1 V3 — never blank, and never the machine code.**
 *
 * Reachable only by a code outside `TENANT_VISIBLE_AUDIT_ACTIONS`, which the two guards named in
 * this file's header are there to make impossible for a producer in the tree. It exists because the
 * alternative — an empty cell — is the defect X10 was raised to fix, and a column that goes blank
 * again must at least say so.
 */
export const UNNAMED_AUDIT_ACTION_HE = 'פעולה שאינה מזוהה';

/**
 * The eighteen names. TOTAL over `TENANT_VISIBLE_AUDIT_ACTIONS` by its type — a missing member is a
 * compile error here, and a member with no Hebrew letter is a failure in `contract.mirror.test.ts`.
 *
 * `authz.allowed` and `authz.denied` SHARE ONE PHRASE, deliberately. The gate row's subject is the
 * DECISION; the `תוצאה` column already says which way it went (`TenantSecurityScreen.tsx`), and the
 * gate structurally does not know the domain target (`apps/api/src/domain/domain-audit.ts:11-15`),
 * so a more specific name would be invented rather than true.
 */
const AUDIT_ACTION_HE: Readonly<Record<TenantVisibleAuditAction, string>> = {
  'authz.allowed': 'בדיקת הרשאה לפעולה',
  'authz.denied': 'בדיקת הרשאה לפעולה',
  'company_access.granted': 'מתן גישה לחברה',
  'company_access.revoked': 'ביטול גישה לחברה',
  'tenant.mfa_policy.set': 'עדכון מדיניות אימות דו-שלבי',
  'companies.company.updated': 'עדכון פרטי חברה',
  'tenant.role.created': 'יצירת תפקיד בארגון',
  'tenant.user.roles.assigned': 'שיוך תפקידים למשתמש',
  'companies.company.created': 'פתיחת חברה',
  'tenant.user.disabled': 'השבתת משתמש',
  'security.support_grant.consented': 'אישור הרשאת תמיכה',
  'security.support_grant.revoked_by_tenant': 'ביטול הרשאת תמיכה',
  'auth.session.signed_out': 'יציאה מהמערכת',
  'auth.invitation.redeemed': 'הצטרפות משתמש בהזמנה',
  'data.cross_tenant.refused': 'ניסיון גישה לנתוני ארגון אחר',
  /*
   * SECTION 7, 2026-10-28. The two acts that record what an inviter chose for somebody who has not
   * arrived. The words say RECORDING and not granting, because that is what the row is: the grant
   * itself happens at redemption and writes its own row under its own code.
   */
  'tenant.invitation.role_intent': 'רישום תפקיד להזמנה',
  'tenant.invitation.access_intent': 'רישום גישה לחברות להזמנה',
  /*
   * THE REFUSAL AT REDEMPTION, 2026-10-28. The office reads this row when a colleague has arrived
   * WITHOUT something that was chosen for them, so the name says which half failed — the authority,
   * not the choice. The missing capability and the invitation are in the row's payload; the name
   * says enough to know that somebody has to decide again, which is the only action available.
   */
  'tenant.invitation.intent_refused': 'הענקה שנרשמה להזמנה נדחתה בהצטרפות',
};

/**
 * The office's name for one audited action.
 *
 * Takes `string`, not `TenantVisibleAuditAction`, ON PURPOSE: the value arrives from the wire, and a
 * narrower parameter here would be a type asserting something about a runtime value nobody checked
 * — which is the exact shape of the defect this module was written to repair.
 */
export function auditActionHe(action: string): string {
  return AUDIT_ACTION_HE[action as TenantVisibleAuditAction] ?? UNNAMED_AUDIT_ACTION_HE;
}

/** For the mirror test: the codes that actually have a name, so totality is checked and not trusted. */
export function mappedAuditActions(): readonly string[] {
  return TENANT_VISIBLE_AUDIT_ACTIONS.filter((code) => AUDIT_ACTION_HE[code] !== undefined);
}
